import { getBackendUrl } from '../utils/apiUrl';
import React, { useEffect, useState, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import devLog from '../utils/devLog';
import { getResultIdFromUrl } from '../hooks/useResultId';

// ═══════════════════════════════════════════════════════════════════════════════
// V24.99.35: SIMPLE LAZY LOADING - Works across all browsers including Safari
// ═══════════════════════════════════════════════════════════════════════════════
const lazyWithRetry = (componentImport, retries = 3, interval = 1000) => {
  return lazy(async () => {
    for (let i = 0; i < retries; i++) {
      try {
        return await componentImport();
      } catch (error) {
        if (i < retries - 1 && error.name === 'ChunkLoadError') {
          await new Promise(resolve => setTimeout(resolve, interval));
          continue;
        }
        throw error;
      }
    }
  });
};

// Lazy load result components
const Result = lazyWithRetry(() => import('./Result').then(module => ({ default: module.Result })));
const StreamingResult = lazyWithRetry(() => import('./StreamingResult').then(module => ({ default: module.StreamingResult })));

const BACKEND_URL = getBackendUrl();

// Simple loading spinner - works on all browsers
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-black border-t-transparent rounded-full animate-spin mx-auto"></div>
    </div>
  </div>
);

export const ResultRouter = ({ Header, Footer }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);
  const [streamingParams, setStreamingParams] = useState(null);
  const [error, setError] = useState(null);
  
  // V24.88.7: NO CACHING - Remove all cache checks from initial state
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadResult = async () => {
      const params = new URLSearchParams(location.search);
      const isStreaming = params.get('streaming') === 'true';
      const isClarified = params.get('clarified') === 'true';
      const modeParam = params.get('mode');
      // V37.32.5: Centralized resolver — same canonical lookup used by
      // every other callsite in the app.
      // V37.43.18: Use non-hook alias to satisfy react-hooks/rules-of-hooks
      // in CI=true production builds. Identical body; no state involved.
      const resultId = getResultIdFromUrl(location.search);
      devLog.log('[ResultRouter] Resolved resultId:', resultId);
      
      devLog.log('[ResultRouter] Loading result with params:', { isStreaming, isClarified, mode: modeParam, resultId });
      
      // If already loaded, don't reload
      if (mode && streamingParams) {
        devLog.log('[ResultRouter] Already loaded, skipping');
        return;
      }
      
      // V24.88.7: NEVER use cached data - ALWAYS require fresh backend data
      // If backend 404, show error. Period. No fallback to cache.
      if (resultId) {
        // NUKE any existing cache for this result
        const cacheKey = `result_${resultId}`;
        localStorage.removeItem(cacheKey);
        sessionStorage.removeItem(cacheKey);
        
        try {
          devLog.log('[ResultRouter V24.88.7] Fetching from backend ONLY:', resultId, 'mode hint:', modeParam);
          // V37.32.6: forward mode hint so backend hits the correct
          // collection first (prevents cross-mode rendering).
          const fetchUrl = modeParam
            ? `${BACKEND_URL}/api/results/${resultId}?mode=${encodeURIComponent(modeParam)}`
            : `${BACKEND_URL}/api/results/${resultId}`;
          const response = await fetch(fetchUrl, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          });
          
          if (response.ok) {
            const result = await response.json();
            devLog.log('[ResultRouter V24.88.7] ✅ Got fresh data from backend; loaded mode:', result.data && result.data.mode);
            // V37.32.6: light client-side guard — warn (don't fail) on
            // mode mismatch so observability catches collection drift.
            if (modeParam && result.data && result.data.mode && result.data.mode !== modeParam) {
              devLog.warn('[ResultRouter] mode mismatch — expected:', modeParam, 'got:', result.data.mode);
            }
            
            if (result.type === 'bug_fix' || result.data.mode === 'bug_fixing') {
              setMode('streaming');
              setStreamingParams({
                input: result.data.user_input || '',
                sessionId: result.data.session_id || '',
                completedResult: result.data
              });
              setLoading(false);
              return;
            } else {
              setMode('streaming');
              setStreamingParams({
                input: result.data.user_input || '',
                sessionId: result.data.session_id || '',
                completedResult: result.data
              });
              setLoading(false);
              return;
            }
          } else {
            devLog.log('[ResultRouter V24.88.7] Backend returned', response.status, '- NO FALLBACK');
          }
        } catch (err) {
          console.error('[ResultRouter V24.88.7] Fetch failed:', err.message);
        }
        
        // NO CACHE FALLBACK - Show error immediately
        setError('This result is no longer available. Please generate a new prompt.');
        setLoading(false);
        return;
      }
      
      // PRIORITY 2: Handle streaming mode from sessionStorage
      if (isStreaming) {
        const input = sessionStorage.getItem('ep_streaming_input');
        const sessionId = sessionStorage.getItem('ep_streaming_session');
        const completedResult = sessionStorage.getItem('ep_completed_result');
        
        devLog.log('[ResultRouter] Streaming mode:', { hasInput: !!input, hasSession: !!sessionId, hasCompleted: !!completedResult, mode: modeParam });
        
        // If we have input and session, or a completed result, show streaming component
        if ((input && sessionId) || completedResult) {
          setMode('streaming');
          setStreamingParams({ 
            input: input || '', 
            sessionId: sessionId || '',
            completedResult: completedResult ? JSON.parse(completedResult) : null
          });
          setLoading(false);
          return;
        }
        
        // V25.2.3: FALLBACK - Check localStorage if sessionStorage is empty
        // This handles page refreshes where sessionStorage is lost
        devLog.log('[ResultRouter V25.2.3] sessionStorage empty, checking localStorage fallback...');
        
        // V25.5.1: Get current session ID for validation
        const currentSessionId = params.get('session_id') || sessionStorage.getItem('ep_streaming_session');
        
        // Check for bug_fixing mode data in localStorage
        if (modeParam === 'bug_fixing') {
          const bugFixData = sessionStorage.getItem('ep_bug_fix_data');
          if (bugFixData) {
            try {
              const parsedData = JSON.parse(bugFixData);
              // V25.5.1: STRICT SESSION CHECK - Only use if session ID matches (prevents stale content)
              if (currentSessionId && parsedData.session_id && parsedData.session_id !== currentSessionId) {
                devLog.warn('[ResultRouter V25.5.1] ⚠️ Session ID mismatch - IGNORING stale bug fix data');
              } else {
                devLog.log('[ResultRouter V25.5.1] ✅ Found valid bug fix data in localStorage');
                setMode('streaming');
                setStreamingParams({
                  input: parsedData.user_input || '',
                  sessionId: parsedData.session_id || '',
                  completedResult: parsedData
                });
                setLoading(false);
                return;
              }
            } catch (e) {
              console.error('[ResultRouter V25.2.3] Failed to parse bug fix data:', e);
            }
          }
        }
        
        // Check for new_project mode data in localStorage
        if (modeParam === 'new_project') {
          const newProjectData = sessionStorage.getItem('ep_new_project_data');
          if (newProjectData) {
            try {
              const parsedData = JSON.parse(newProjectData);
              // V25.5.1: STRICT SESSION CHECK - Only use if session ID matches (prevents stale content)
              if (currentSessionId && parsedData.session_id && parsedData.session_id !== currentSessionId) {
                devLog.warn('[ResultRouter V25.5.1] ⚠️ Session ID mismatch - IGNORING stale new project data');
              } else {
                devLog.log('[ResultRouter V25.5.1] ✅ Found valid new project data in localStorage');
                setMode('streaming');
                setStreamingParams({
                  input: parsedData.user_input || '',
                  sessionId: parsedData.session_id || '',
                  completedResult: parsedData
                });
                setLoading(false);
                return;
              }
            } catch (e) {
              console.error('[ResultRouter V25.2.3] Failed to parse new project data:', e);
            }
          }
        }
        
        // Check for latest result as last resort - STRICT MODE AND SESSION CHECK
        const latestResult = sessionStorage.getItem('ep_latest_result');
        if (latestResult) {
          devLog.log('[ResultRouter V25.5.1] Checking latest result in localStorage');
          try {
            const parsedData = JSON.parse(latestResult);
            // V25.5.1: STRICT MODE + SESSION CHECK - Only use if BOTH match
            const modeMatches = parsedData.mode === modeParam;
            const sessionMatches = !currentSessionId || !parsedData.session_id || parsedData.session_id === currentSessionId;
            
            if (modeMatches && sessionMatches) {
              devLog.log('[ResultRouter V25.5.1] ✅ Mode and session match, using latest result');
              setMode('streaming');
              setStreamingParams({
                input: parsedData.user_input || '',
                sessionId: parsedData.session_id || '',
                completedResult: parsedData
              });
              setLoading(false);
              return;
            } else {
              devLog.warn(`[ResultRouter V25.5.1] ⚠️ Validation failed: mode=${modeMatches}, session=${sessionMatches} - IGNORING stale data`);
            }
          } catch (e) {
            console.error('[ResultRouter V25.2.3] Failed to parse latest result:', e);
          }
        }
        
        // If streaming but no data found, show error
        devLog.log('[ResultRouter V25.2.3] ❌ No streaming data found in session or local storage');
        setError('Streaming session expired. Please generate a new prompt.');
        setLoading(false);
        return;
      }
      
      // PRIORITY 3: Handle bug fixing mode - ALWAYS use StreamingResult with completedResult
      if (modeParam === 'bug_fixing' && !isStreaming) {
        const bugFixData = sessionStorage.getItem('ep_bug_fix_data');
        if (bugFixData) {
          devLog.log('[ResultRouter] ✅ Bug Fix from URL - Using StreamingResult with completedResult');
          try {
            const parsedData = JSON.parse(bugFixData);
            setMode('streaming');
            setStreamingParams({
              input: parsedData.user_input || '',
              sessionId: parsedData.session_id || '',
              completedResult: parsedData
            });
            setLoading(false);
            return;
          } catch (e) {
            console.error('[ResultRouter] Failed to parse bug fix data:', e);
            setError('Bug fix data corrupted. Please generate a new bug fix.');
            setLoading(false);
            return;
          }
        } else {
          setError('Bug fix data not found. Please generate a new bug fix.');
          setLoading(false);
          return;
        }
      }
      
      // PRIORITY 4: Handle clarified result
      if (isClarified) {
        setMode('static');
        setLoading(false);
        return;
      }
      
      // PRIORITY 5: Try to load from localStorage for static mode
      const latestResult = sessionStorage.getItem('ep_latest_result');
      if (latestResult) {
        setMode('static');
        setLoading(false);
        return;
      }
      
      // FALLBACK: No data found anywhere
      setError('No result data found. Please generate a new prompt.');
      setLoading(false);
    };
    
    loadResult();
  }, [location, mode, streamingParams]);
  
  const handleStreamComplete = (result) => {
    let finalMode = result.mode || result.type || 'new_project';
    
    try {
      if (result.id) {
        sessionStorage.setItem('ep_current_result', JSON.stringify(result));
      }
      sessionStorage.removeItem('ep_streaming_input');
      sessionStorage.removeItem('ep_streaming_session');
      
      if (finalMode === 'bug_fixing') {
        sessionStorage.removeItem('ep_new_project_data');
        sessionStorage.setItem('ep_bug_fix_data', JSON.stringify(result));
      } else {
        sessionStorage.removeItem('ep_bug_fix_data');
        sessionStorage.setItem('ep_new_project_data', JSON.stringify(result));
      }
      
      const resultWithMode = { ...result, mode: finalMode };
      sessionStorage.setItem('ep_latest_result', JSON.stringify(resultWithMode));
    } catch (error) {
      devLog.warn('[ResultRouter] Storage error:', error.message);
    }
    
    // SAFE-SYNC URL: Only update URL with real result ID, not force_/recovery_ IDs.
    // This prevents navigating to URLs that return 404 on refresh.
    const rid = result.id || '';
    if (rid && !rid.startsWith('force_') && !rid.startsWith('recovery_')) {
      const cleanUrl = `/result?mode=${finalMode}&result_id=${rid}`;
      window.history.replaceState(null, '', cleanUrl);
    } else {
      // Keep current URL — content is rendered from local buffer
      window.history.replaceState(null, '', `/result?mode=${finalMode}`);
    }
  };
  
  if (loading) {
    // PERFORMANCE FIX: Minimal loading indicator (cache check makes this rarely visible)
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-gray-300 border-t-black rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Result Not Found</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500 mb-6">
            This may be an older result that wasn&apos;t saved. Please generate a new prompt to see your results persist correctly.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-200"
          >
            Generate New Prompt
          </button>
        </div>
      </div>
    );
  }
  
  if (mode === 'streaming' && streamingParams) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <StreamingResult
          Header={Header}
          Footer={Footer}
          userInput={streamingParams.input}
          sessionId={streamingParams.sessionId}
          onComplete={handleStreamComplete}
          completedResult={streamingParams.completedResult}
        />
      </Suspense>
    );
  }
  
  // Fallback: Static result page
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Result Header={Header} Footer={Footer} />
    </Suspense>
  );
};

export default ResultRouter;
